import { Flex, Text, Button, Select, Card, Heading, Box, Badge, Tooltip } from '@radix-ui/themes';
import React, { useState, useEffect, useRef } from 'react';
import { BiPlay, BiStop, BiSave, BiCog } from 'react-icons/bi';
import { TbSettingsCancel, TbGripVertical } from "react-icons/tb";
import axiosInstance, { BASE_URL } from '../config/axiosConfig';
import { HexColorPicker } from 'react-colorful';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AdvancedAlarmSettings from '../components/ui-components/AdvancedAlarmSettings';

const alarmGridTemplateColumns = '48px minmax(76px, .4fr) minmax(180px, 1fr) minmax(260px, 1.4fr) minmax(72px, .45fr) minmax(92px, .45fr) minmax(132px, .65fr)';

const alarmGridStyle = {
  display: 'grid',
  gridTemplateColumns: alarmGridTemplateColumns,
  columnGap: '16px',
  alignItems: 'center',
  minWidth: '960px'
};

const hiddenTuneFileNames = new Set(['extension.mp3', 'sos.mp3']);

const isSelectableTune = (tune) => {
  const fileName = (tune?.value || '').toLowerCase();
  return fileName && !hiddenTuneFileNames.has(fileName);
};

const getTuneAudioUrl = (tuneId, availableTunes) => {
  if (!tuneId) return null;

  const tune = availableTunes.find((option) => option.value === tuneId);
  const audioPath = tune?.audioPath || `/uploads/tunes/${encodeURIComponent(tuneId)}`;

  if (/^https?:\/\//i.test(audioPath)) {
    return audioPath;
  }

  return `${BASE_URL.replace(/\/$/, '')}/${audioPath.replace(/^\/+/, '')}`;
};

const buildSettingsSnapshot = (nextSettings, nextOrder) => JSON.stringify({
  order: nextOrder,
  settings: nextOrder.reduce((snapshot, alarmId) => {
    const setting = nextSettings[alarmId] || {};
    snapshot[alarmId] = {
      tuneId: setting.tuneId || null,
      color: setting.color,
      timeOutOnNotification: setting.timeOutOnNotification,
      timeOutOnConfirmation: setting.timeOutOnConfirmation,
      timeOutOnHold: setting.timeOutOnHold,
      isHoldTimeExtension: setting.isHoldTimeExtension,
      issueWarningBefore: setting.issueWarningBefore,
      priority: setting.priority
    };
    return snapshot;
  }, {})
});

const AlarmSettingsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [playingAlarmId, setPlayingAlarmId] = useState(null);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  const [currentAlarmId, setCurrentAlarmId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [colorPickerPosition, setColorPickerPosition] = useState({});
  const audioRef = useRef(null);
  const colorBoxRefs = useRef({});
  const colorPickerRefs = useRef({});

  // Mapping of masterAlarmId to display names and backend alarm types
  const alarmIdMapping = {
    1: { displayName: 'Diaper', backendAlarmType: 'WetCall' },
    2: { displayName: 'Wake Up', backendAlarmType: 'WakeUpCall' },
    3: { displayName: 'Nurse Call', backendAlarmType: 'NurseCall' },
    9: { displayName: 'Medication', backendAlarmType: 'Medication' },
    4: { displayName: 'Rolling', backendAlarmType: 'RollCall' },
    8: { displayName: 'Patient Out Of Room', backendAlarmType: 'OutofRoom' },
    7: { displayName: 'Housekeeping', backendAlarmType: 'Laundary' },
    6: { displayName: 'Security', backendAlarmType: 'Security' },
    5: { displayName: 'F&B', backendAlarmType: 'Food' }
  };

  // Initial settings with masterAlarmId as keys
  const [settings, setSettings] = useState({
    1: { 
      tuneId: null, 
      color: '#00B893', 
      timeOutOnNotification: 5, 
      timeOutOnConfirmation: 5, 
      timeOutOnHold: 5, 
      isHoldTimeExtension: 5,
      issueWarningBefore: 2,
      priority: 1 
    },
    2: { 
      tuneId: null, 
      color: '#FF6B09', 
      timeOutOnNotification: 5, 
      timeOutOnConfirmation: 5, 
      timeOutOnHold: 5, 
      isHoldTimeExtension: 5,
      issueWarningBefore: 2,
      priority: 2 
    },
    3: { 
      tuneId: null, 
      color: '#CC0202', 
      timeOutOnNotification: 5, 
      timeOutOnConfirmation: 5, 
      timeOutOnHold: 5, 
      isHoldTimeExtension: 5,
      issueWarningBefore: 2,
      priority: 3 
    },
    9: { 
      tuneId: null, 
      color: '#0099FF', 
      timeOutOnNotification: 5, 
      timeOutOnConfirmation: 5, 
      timeOutOnHold: 5, 
      isHoldTimeExtension: 5,
      issueWarningBefore: 2,
      priority: 4 
    },
    4: { 
      tuneId: null, 
      color: '#5B66CB', 
      timeOutOnNotification: 5, 
      timeOutOnConfirmation: 5, 
      timeOutOnHold: 5, 
      isHoldTimeExtension: 5,
      issueWarningBefore: 2,
      priority: 5 
    },
    8: { 
      tuneId: null, 
      color: '#563d7c', 
      timeOutOnNotification: 5, 
      timeOutOnConfirmation: 5, 
      timeOutOnHold: 5, 
      isHoldTimeExtension: 5,
      issueWarningBefore: 2,
      priority: 6 
    },
    7: { 
      tuneId: null, 
      color: '#FFD700', 
      priority: 7 
    },
    6: { 
      tuneId: null, 
      color: '#FF4500', 
      priority: 8 
    },
    5: { 
      tuneId: null, 
      color: '#ADFF2F', 
      priority: 9 
    },
  });

  const [availableTunes, setAvailableTunes] = useState([]);
  const [alarmOrder, setAlarmOrder] = useState(Object.keys(settings).map(Number));
  const [savedSettingsSnapshot, setSavedSettingsSnapshot] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      let nextSettings = settings;
      let nextOrder = alarmOrder;

      try {
        setIsLoading(true);
        
        // Fetch available tunes
        const tunesResponse = await axiosInstance.get('/alarm-settings/available-tunes');
        if (tunesResponse.data.result) {
          setAvailableTunes(tunesResponse.data.result.filter(isSelectableTune));
        } else {
          toast.error('Failed to load available tunes');
        }
        
        // Fetch existing settings
        const settingsResponse = await axiosInstance.get('/alarm-settings/get');
        if (settingsResponse.data.result) {
          const existingSettings = {};
          
          // Use masterAlarmId as key
          settingsResponse.data.result.forEach(setting => {
            const alarmId = setting.masterAlarmId;
            if (!alarmIdMapping[alarmId]) {
              console.warn(`Unmapped masterAlarmId received from backend: ${alarmId}`);
              return;
            }
            existingSettings[alarmId] = {
              tuneId: setting.tunePath || null,
              color: setting.color || settings[alarmId]?.color || '#CCCCCC',
              timeOutOnNotification: setting.timeOutOnNotification || 5,
              timeOutOnConfirmation: setting.timeOutOnConfirmation || 5,
              timeOutOnHold: setting.timeOutOnHold || 5,
              isHoldTimeExtension: setting.isHoldTimeExtension || 5,
              issueWarningBefore: setting.issueWarningBefore || 2,
              priority: setting.priority || settings[alarmId]?.priority || 999
            };
          });
          
          // Merge with default settings and sort by priority
          const mergedSettings = { ...settings, ...existingSettings };
          const sortedAlarms = Object.keys(mergedSettings)
            .map(Number)
            .filter(id => alarmIdMapping[id])
            .sort((a, b) => mergedSettings[a].priority - mergedSettings[b].priority);
          
          nextSettings = mergedSettings;
          nextOrder = sortedAlarms;
          setSettings(mergedSettings);
          setAlarmOrder(sortedAlarms);
        }
      } catch (error) {
        toast.error('Error loading configuration data');
        console.error(error);
      } finally {
        setSavedSettingsSnapshot(buildSettingsSnapshot(nextSettings, nextOrder));
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeColorPicker !== null) {
        const colorBoxElement = colorBoxRefs.current[activeColorPicker];
        const colorPickerElement = colorPickerRefs.current[activeColorPicker];
        
        // Check if click is outside both the color box and the color picker
        if (
          colorBoxElement && 
          !colorBoxElement.contains(event.target) &&
          colorPickerElement &&
          !colorPickerElement.contains(event.target)
        ) {
          setActiveColorPicker(null);
          setColorPickerPosition({});
        }
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeColorPicker]);

  const handleTuneChange = (alarmId, value) => {
    setSettings(prev => ({
      ...prev,
      [alarmId]: { ...prev[alarmId], tuneId: value === 'none' ? null : value },
    }));
  };

  const handleColorChange = (alarmId, color) => {
    setSettings(prev => ({
      ...prev,
      [alarmId]: { ...prev[alarmId], color },
    }));
  };

  const updatePriorities = (newOrder) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      newOrder.forEach((alarmId, index) => {
        newSettings[alarmId] = { ...newSettings[alarmId], priority: index + 1 };
      });
      return newSettings;
    });
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedItem === null || draggedItem === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const newOrder = [...alarmOrder];
    const draggedAlarmId = newOrder[draggedItem];
    
    newOrder.splice(draggedItem, 1);
    newOrder.splice(dropIndex, 0, draggedAlarmId);
    
    setAlarmOrder(newOrder);
    updatePriorities(newOrder);
    setDragOverIndex(null);
  };

  const toggleColorPicker = (alarmId) => {
    if (activeColorPicker === alarmId) {
      setActiveColorPicker(null);
      setColorPickerPosition({});
    } else {
      setActiveColorPicker(alarmId);
      
      // Calculate position dynamically
      const colorBoxElement = colorBoxRefs.current[alarmId];
      if (colorBoxElement) {
        const rect = colorBoxElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const colorPickerHeight = 250; // Approximate height of color picker with padding
        
        // Check if there's enough space below
        const spaceBelow = viewportHeight - rect.bottom;
        const shouldShowAbove = spaceBelow < colorPickerHeight;
        
        setColorPickerPosition({
          [alarmId]: shouldShowAbove ? 'above' : 'below'
        });
      }
    }
  };

  const togglePlayStop = (alarmId, tuneId) => {
    if (playingAlarmId === alarmId) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      setPlayingAlarmId(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      const audioUrl = getTuneAudioUrl(tuneId, availableTunes);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setPlayingAlarmId(null);
        audioRef.current = null;
      };
      audio.onerror = () => {
        console.error(`Unable to load alarm tune: ${audioUrl}`);
        toast.error('Unable to play selected tune');
        setPlayingAlarmId(null);
        audioRef.current = null;
      };

      audio.play()
        .then(() => setPlayingAlarmId(alarmId))
        .catch((error) => {
          console.error('Error playing alarm tune:', error);
          toast.error('Unable to play selected tune');
          setPlayingAlarmId(null);
          audioRef.current = null;
        });
    }
  };

  const handleAdvancedSettings = (alarmId) => {
    setCurrentAlarmId(alarmId);
    setAdvancedSettingsOpen(true);
  };

  const handleAdvancedSettingsSave = (updatedSettings) => {
    if (currentAlarmId) {
      setSettings(prev => ({
        ...prev,
        [currentAlarmId]: { ...prev[currentAlarmId], ...updatedSettings },
      }));
    }
    setAdvancedSettingsOpen(false);
  };

  const hasTimeoutFields = (alarmId) => {
    return [1, 2, 3, 9, 4, 8].includes(alarmId);
  };

  const getFormattedAlarmType = (alarmId) => {
    return alarmIdMapping[alarmId]?.displayName || 'Unknown';
  };

  const currentSettingsSnapshot = buildSettingsSnapshot(settings, alarmOrder);
  const hasUnsavedChanges = savedSettingsSnapshot !== null && savedSettingsSnapshot !== currentSettingsSnapshot;

  const handleSaveSettings = async () => {
    try {
      const settingsPayload = alarmOrder.map((alarmId, index) => {
        const alarmSetting = settings[alarmId];
        const backendAlarmType = alarmIdMapping[alarmId]?.backendAlarmType;
        
        if (!backendAlarmType) {
          console.error(`No backend alarm type found for alarmId: ${alarmId}`);
          return null;
        }
        
        return {
          masterAlarmId: alarmId,
          alarmType: backendAlarmType,
          tunePath: alarmSetting.tuneId,
          color: alarmSetting.color,
          priority: index + 1,
          isEnabled: true,
          ...(hasTimeoutFields(alarmId) && {
            timeOutOnNotification: alarmSetting.timeOutOnNotification,
            timeOutOnConfirmation: alarmSetting.timeOutOnConfirmation,
            timeOutOnHold: alarmSetting.timeOutOnHold,
            isHoldTimeExtension: alarmSetting.isHoldTimeExtension,
            issueWarningBefore: alarmSetting.issueWarningBefore,
          }),
        };
      }).filter(setting => setting !== null);

      const response = await axiosInstance.post('/alarm-settings/save', settingsPayload);
      
      if (response.data.result) {
        setSavedSettingsSnapshot(currentSettingsSnapshot);
        toast.success('Settings saved successfully');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error saving settings');
    }
  };

  if (isLoading) {
    return (
      <Flex 
        direction="column" 
        align="center" 
        justify="center" 
        style={{ minHeight: '400px' }}
      >
        <Text size="4">Loading alarm settings...</Text>
      </Flex>
    );
  }

  return (
    <>
      <Card
        size="4"
        style={{
          width: '100%',
          maxWidth: '1500px',
          margin: '0 auto 24px',
          boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)'
        }}
      >
        <Flex direction="column" gap="4">
          <Flex justify="between" align="center" gap="3" wrap="wrap">
            <Flex direction="column" gap="1">
              <Flex align="center" gap="2" wrap="wrap">
                <Heading size="6">Alarm Settings</Heading>
                {hasUnsavedChanges && (
                  <Badge color="amber" variant="soft">
                    Unsaved changes
                  </Badge>
                )}
              </Flex>
              <Text size="2" color="gray">
                Drag the handle to reorder priorities. Changes apply system-wide for your institution.
              </Text>
            </Flex>

            <Tooltip content={hasUnsavedChanges ? 'Save alarm settings' : 'No changes to save'}>
              <Button
                size="3"
                onClick={handleSaveSettings}
                disabled={!hasUnsavedChanges}
                style={{ minWidth: '132px' }}
              >
                <BiSave /> Save Settings
              </Button>
            </Tooltip>
          </Flex>

          <Box style={{ overflowX: 'auto', paddingBottom: '2px' }}>
            <Box
              style={{ 
                ...alarmGridStyle,
                backgroundColor: 'var(--gray-2)',
                borderRadius: '6px',
                marginBottom: '8px',
                fontWeight: 500,
                padding: '10px 12px'
              }}
            >
              <Text size="2" style={{ textAlign: 'center' }}></Text>
              <Text size="2" style={{ textAlign: 'center' }}>Priority</Text>
              <Text size="2">Alarm Type</Text>
              <Text size="2">Tune Selection</Text>
              <Text size="2" style={{ textAlign: 'center' }}>Preview</Text>
              <Text size="2" style={{ textAlign: 'center' }}>Color</Text>
              <Text size="2" style={{ textAlign: 'center' }}>Advanced</Text>
            </Box>
            
            {alarmOrder.map((alarmId, index) => {
              const { tuneId, color, priority } = settings[alarmId] || {};
              if (!alarmIdMapping[alarmId]) return null; // Skip unmapped IDs
              const isPlaying = playingAlarmId === alarmId;
              const isDragOver = dragOverIndex === index;
              const pickerPosition = colorPickerPosition[alarmId] || 'below';
              
              return (
                <Box
                  key={alarmId} 
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  style={{ 
                    ...alarmGridStyle,
                    borderBottom: '1px solid var(--gray-4)',
                    backgroundColor: isDragOver 
                      ? 'var(--blue-3)' 
                      : index % 2 === 0 ? 'transparent' : 'var(--gray-1)',
                    borderRadius: '4px',
                    margin: '2px 0',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                    border: isDragOver ? '2px dashed var(--blue-8)' : '2px solid transparent',
                    userSelect: 'none',
                    padding: '12px'
                  }}
                >
                  <Tooltip content={`Move ${getFormattedAlarmType(alarmId)}`}>
                    <Flex
                      align="center"
                      justify="center"
                      draggable="true"
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnd={handleDragEnd}
                      role="button"
                      tabIndex={0}
                      aria-label={`Move ${getFormattedAlarmType(alarmId)}`}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '6px',
                        cursor: 'grab',
                        color: 'var(--gray-9)'
                      }}
                    >
                      <TbGripVertical size={22} style={{ opacity: 0.75 }} />
                    </Flex>
                  </Tooltip>

                  <Flex align="center" justify="center">
                    <Badge color="blue" variant="soft" style={{ minWidth: '24px', textAlign: 'center' }}>
                      {priority}
                    </Badge>
                  </Flex>
                  
                  <Text 
                    as="div" 
                    size="3" 
                    style={{ 
                      fontWeight: 500,
                      pointerEvents: 'none'
                    }}
                  >
                    {getFormattedAlarmType(alarmId)}
                  </Text>
                  
                  <Box style={{ pointerEvents: 'all' }}>
                    <Select.Root
                      value={tuneId || 'none'}
                      onValueChange={(value) => handleTuneChange(alarmId, value)}
                      size="2"
                    >
                      <Select.Trigger 
                        placeholder="Select Tune" 
                        style={{ width: '100%' }}
                      />
                      <Select.Content>
                        <Select.Group>
                          <Select.Item value="none">None</Select.Item>
                          {availableTunes.map((option) => (
                            <Select.Item key={option.value} value={option.value}>
                              {option.label}
                            </Select.Item>
                          ))}
                        </Select.Group>
                      </Select.Content>
                    </Select.Root>
                  </Box>
                  
                  <Flex align="center" justify="center" style={{ pointerEvents: 'all' }}>
                    <Tooltip content={tuneId ? (isPlaying ? 'Stop preview' : 'Play preview') : 'Select a tune to preview'}>
                      <Button
                        variant={isPlaying ? "solid" : "soft"}
                        color={isPlaying ? "crimson" : "gray"}
                        onClick={() => togglePlayStop(alarmId, tuneId)}
                        disabled={!tuneId}
                        size="2"
                        aria-label={isPlaying ? 'Stop preview' : 'Play preview'}
                        style={{
                          width: '40px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {isPlaying ? <BiStop /> : <BiPlay />}
                      </Button>
                    </Tooltip>
                  </Flex>
                  
                  <Flex 
                    direction="column" 
                    position="relative" 
                    align="center"
                    style={{ pointerEvents: 'all' }}
                    ref={(el) => colorBoxRefs.current[alarmId] = el}
                  >
                    <Tooltip content={`Change ${getFormattedAlarmType(alarmId)} color`}>
                      <button
                        type="button"
                        aria-label={`Change ${getFormattedAlarmType(alarmId)} color`}
                        onClick={() => toggleColorPicker(alarmId)}
                        style={{ 
                          width: '36px', 
                          height: '36px', 
                          backgroundColor: color,
                          borderRadius: '6px',
                          border: '2px solid var(--gray-5)',
                          cursor: 'pointer',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          padding: 0
                        }} 
                      />
                    </Tooltip>
                    
                    {activeColorPicker === alarmId && (
                      <Box 
                        ref={(el) => colorPickerRefs.current[alarmId] = el}
                        style={{ 
                          position: 'absolute', 
                          ...(pickerPosition === 'above' 
                            ? { bottom: '45px' } 
                            : { top: '45px' }
                          ),
                          left: '-80px',
                          zIndex: 10,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          borderRadius: '8px',
                          padding: '12px',
                          backgroundColor: 'white',
                          border: '1px solid var(--gray-4)'
                        }}
                      >
                        <HexColorPicker
                          color={color}
                          onChange={(newColor) => handleColorChange(alarmId, newColor)}
                          style={{ width: '200px', height: '200px' }}
                        />
                      </Box>
                    )}
                  </Flex>

                  <Flex justify="center" style={{ pointerEvents: 'all' }}>
                    {hasTimeoutFields(alarmId) ? (
                      <Tooltip content={`Edit ${getFormattedAlarmType(alarmId)} timeouts`}>
                        <Button 
                          variant="soft" 
                          size="2"
                          onClick={() => handleAdvancedSettings(alarmId)}
                          aria-label={`Edit ${getFormattedAlarmType(alarmId)} timeouts`}
                          style={{ width: '40px' }}
                        >
                          <BiCog />
                        </Button>
                      </Tooltip>
                    ) : (
                      <Badge color="gray" variant="soft">
                        <TbSettingsCancel />
                        Not applicable
                      </Badge>
                    )}
                  </Flex>
                </Box>
              );
            })}
          </Box>
        </Flex>
      </Card>

      <AdvancedAlarmSettings
        isOpen={advancedSettingsOpen}
        onClose={() => setAdvancedSettingsOpen(false)}
        alarmType={currentAlarmId ? alarmIdMapping[currentAlarmId]?.displayName : null}
        settings={currentAlarmId ? settings[currentAlarmId] : null}
        onSave={handleAdvancedSettingsSave}
      />
    </>
  );
};

export default AlarmSettingsPage;
